import * as React from 'react';
import { useState } from 'react';

import { Badge, Button, Heading, Icon, Input, notify, Surface, Text } from '@/components/ui';

import { generatePassword, validatePassword } from './passwordUtils';

// PasswordSetup — self-contained widget for choosing a new password.
// Owns the two password inputs, runs the requirements checklist
// against them, generates a random password on demand, and reveals
// the inputs once a password is generated so the user can read what
// just landed there.
//
// Single output: `onChange({ password, isValid })`. The caller stores
// the password (to submit) and the validity (to gate the submit
// button). When the user breaks validity (e.g. backspaces past a
// requirement), the next onChange call carries `isValid: false`, so
// the submit button disables in lockstep.
//
// `passwordUtils.js` is consumed only by this component — there is
// no need for the caller to import or know about it.

export function PasswordSetup({ onChange }) {
    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    // `revealPasswords` flips to true after the user clicks Generate
    // so the freshly minted password is immediately legible. The
    // change of `key` on the inputs forces a remount that picks up
    // `defaultVisible={true}` — Input doesn't expose a controlled
    // `visible` prop on purpose (see the password section in
    // Input.jsx), so this is the established way to flip it.
    const [revealPasswords, setRevealPasswords] = useState(false);

    const requirements = validatePassword(password, passwordConfirmation);
    const isValid = Object.values(requirements).every(req => req.met);

    const notifyChange = (nextPassword, nextConfirmation) => {
        const nextRequirements = validatePassword(nextPassword, nextConfirmation);
        const nextIsValid = Object.values(nextRequirements).every(req => req.met);
        onChange?.({ password: nextPassword, isValid: nextIsValid });
    };

    const handlePasswordChange = (event) => {
        const nextPassword = event.target.value;
        setPassword(nextPassword);
        notifyChange(nextPassword, passwordConfirmation);
    };

    const handleConfirmationChange = (event) => {
        const nextConfirmation = event.target.value;
        setPasswordConfirmation(nextConfirmation);
        notifyChange(password, nextConfirmation);
    };

    const handleGenerate = async () => {
        const generated = generatePassword();
        setPassword(generated);
        setPasswordConfirmation(generated);
        setRevealPasswords(true);
        notifyChange(generated, generated);

        if (!navigator.clipboard?.writeText) {
            notify.info(i18next.t('ui.passwords.generated'));
            return;
        }

        try {
            await navigator.clipboard.writeText(generated);
            notify.info(i18next.t('ui.passwords.copied_to_clipboard'));
        } catch (error) {
            notify.info(i18next.t('ui.passwords.generated'));
        }
    };

    const inputKey = revealPasswords ? 'visible' : 'hidden';

    return (
        <div className="tw-flex tw-flex-col tw-gap-3">
            <Input
                key={`password-${inputKey}`}
                defaultVisible={revealPasswords}
                onChange={handlePasswordChange}
                placeholder={i18next.t('common.new_password').capitalize()}
                type="password"
                value={password}
            />
            <Input
                key={`confirmation-${inputKey}`}
                defaultVisible={revealPasswords}
                onChange={handleConfirmationChange}
                placeholder={i18next.t('glossary.password_confirmation').capitalize()}
                type="password"
                value={passwordConfirmation}
            />
            <div>
                <Badge
                    label={i18next.t(isValid ? 'ui.passwords.requirements.met' : 'ui.passwords.requirements.pending')}
                    tone={isValid ? 'success' : 'warning'}
                />
            </div>
            <Surface tone="info">
                <div className="tw-flex tw-flex-col tw-gap-3">
                    <Heading as="h5" tone="inherit" variant="title-sm">
                        {i18next.t('ui.passwords.requirements.title')}
                    </Heading>
                    <ul className="tw-flex tw-flex-col tw-gap-2 tw-list-none tw-p-0 tw-m-0">
                        {Object.entries(requirements).map(([key, { met, message }]) => (
                            <li key={key} className="tw-flex tw-items-center tw-gap-2 tw-w-full">
                                {met ? (
                                    <Icon icon="circleCheck" size="small" tone="success" />
                                ) : (
                                    <Icon icon="exclamationCircle" size="small" tone="destructive" />
                                )}
                                <Text variant="body-sm">{message}</Text>
                            </li>
                        ))}
                    </ul>
                    <Button
                        appearance="outline"
                        fullWidth
                        onClick={handleGenerate}
                        tone="info"
                        type="button"
                    >
                        {i18next.t('ui.passwords.generate')}
                    </Button>
                </div>
            </Surface>
        </div>
    );
}
